import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Button, Input } from "reactstrap";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { formatCurrency } from "../Utils/Currency";
import { getAllChargingSession } from "../../slices/EVChargingSessionReport/thunk";
// import * as XLSX from "xlsx";
import * as XLSX from "xlsx-js-style";
import { Loader } from "../Utils/Loader";

/* ------------------------------ HELPERS ------------------------------ */

const fmtDuration = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === "") {
    return "-";
  }

  const totalSeconds = Number(seconds);

  if (Number.isNaN(totalSeconds)) {
    return "-";
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${String(remainingSeconds).padStart(2, "0")}s`;
  }

  return `${remainingSeconds}s`;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "-";

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const formatValue = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return value;
  }

  return number.toFixed(decimalPlaces);
};

const headStyle = {
  sx: {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#111827",
    whiteSpace: "nowrap",
    backgroundColor: "#fff",

    // Keep heading first and sorting icon next
    "& .Mui-TableHeadCell-Content": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
    },

    "& .Mui-TableHeadCell-Content-Labels": {
      order: 1,
      flex: "0 0 auto",
    },

    "& .MuiTableSortLabel-root": {
      order: 2,
      marginLeft: "4px",
      marginRight: "0px",
    },

    "& .MuiTableSortLabel-icon": {
      marginLeft: "2px",
      marginRight: "0px",
    },
  },
};

const bodyStyle = {
  sx: {
    fontSize: "12px",
    color: "#111827",
    whiteSpace: "nowrap",
    py: "8px",
  },
};

function EVChargingSessionReport() {
  const dispatch = useDispatch();

  const { chargingSessionReport = [], totalCount = 0 } = useSelector(
    (state) => state.GetAllChargingSession || {},
  );

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 30,
  });

  const getCurrentMonthStart = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
  };

  const getToday = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate(),
    ).padStart(2, "0")}`;
  };

  const [fromDate, setFromDate] = useState(getCurrentMonthStart());
  const [toDate, setToDate] = useState(getToday());

  /* ------------------------------ API CALL ------------------------------ */

  useEffect(() => {
    const filter = {
      ...(fromDate && {
        fromDate: `${fromDate}T00:00:00Z`,
      }),
      ...(toDate && {
        toDate: `${toDate}T00:00:00Z`,
      }),
      ...(searchText.trim() && {
        search: searchText.trim(),
      }),
    };

    dispatch(
      getAllChargingSession(
        filter,
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        setLoading,
      ),
    );
  }, [
    dispatch,
    fromDate,
    toDate,
    searchText,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  /* ------------------------------ TABLE DATA ------------------------------ */

  const data = useMemo(() => {
    return chargingSessionReport.map((session) => ({
      id: session.sessionId || "-",
      stationId: session.stationId || "-",
      stationName: session.stationName || "-",
      connector: session.connectorId || "-",
      user: session.customerMobileNumber || "-",

      start: formatTimestamp(session.startTimestamp) || "-",
      end: formatTimestamp(session.endTimestamp) || "-",

      durationMin: session.duration || "-",

      energy: session.energyDelivered || "-",

      startSoc: session.startSoc,
      endSoc: session.endSoc,

      maxKw: session.maxChargingPower || "-",
      avgKw: session.avgChargingPower || "-",

      status: session.sessionStatus || "-",
      stopReason: session.stopReason || "-",

      tariff: session.tariffPlanApplied ? "Global Charge and Commute" : "-",

      amount: session.amountCharged || "-",
      tax: session.tax || "-",
      total: session.totalAmount || "-",
    }));
  }, [chargingSessionReport]);

  /* ------------------------------ SUMMARY CARDS ------------------------------ */

  const summary = useMemo(() => {
    const completedCount = chargingSessionReport.filter(
      (session) => session.sessionStatus === "Success",
    ).length;

    const energyDelivered = chargingSessionReport.reduce(
      (total, session) => total + Number(session.energyDelivered || 0),
      0,
    );

    const totalRevenue = chargingSessionReport.reduce(
      (total, session) => total + Number(session.totalAmount || 0),
      0,
    );

    const successRate =
      chargingSessionReport.length > 0
        ? ((completedCount / chargingSessionReport.length) * 100).toFixed(0)
        : 0;

    return [
      {
        label: "TOTAL SESSIONS",
        value: totalCount,
        note: "in selected period",
      },
      {
        label: "COMPLETED",
        value: completedCount,
        note: `${successRate}% success rate`,
      },
      {
        label: "ENERGY DELIVERED",
        value: `${energyDelivered.toFixed(2)} kWh`,
        note: "total transferred",
      },
      {
        label: "TOTAL REVENUE",
        value: formatCurrency(totalRevenue),
        note: "incl. tax",
      },
    ];
  }, [chargingSessionReport, totalCount]);

  /* ------------------------------ RESET ------------------------------ */

  const handleReset = () => {
    setFromDate(getCurrentMonthStart());
    setToDate(getToday());
    setSearchText("");

    setPagination((p) => ({
      ...p,
      pageIndex: 0,
    }));
  };

  /* ------------------------------ DOWNLOAD ------------------------------ */

  const handleDownload = () => {
    const headers = [
      "Session ID",
      "Station ID",
      "Station Name",
      "Connector ID",
      "Mobile Number",
      "Start Timestamp",
      "End Timestamp",
      "Duration",
      "Energy Delivered (kWh)",
      "Start SOC (%)",
      "End SOC (%)",
      "Max/Avg Charging Power (kW)",
      // "Session Status",
      "Stop Reason",
      "Tariff Plan Applied",
      "Cost/Amount Charged",
      "Tax",
      "Total Amount",
    ];

    // Use the complete Redux data directly
    const exportRows = chargingSessionReport
      .map((session) => [
        session.sessionId || "-",
        session.stationId || "-",
        session.stationName || "-",
        session.connectorId || "-",
        session.customerMobileNumber || "-",
        formatTimestamp(session.startTimestamp) || "-",
        formatTimestamp(session.endTimestamp) || "-",
        fmtDuration(session.duration),
        formatValue(session.energyDelivered, 4),
        session.startSoc !== null && session.startSoc !== undefined
          ? `${session.startSoc}%`
          : "-",
        session.endSoc !== null && session.endSoc !== undefined
          ? `${session.endSoc}%`
          : "-",
        `${formatValue(session.maxChargingPower, 2)} / ${formatValue(
          session.avgChargingPower,
          2,
        )}`,
        session.sessionStatus || "-",
        session.stopReason || "-",
        session.tariffPlanApplied ? "Global Charge and Commute" : "-",
        formatValue(session.amountCharged, 4),
        formatValue(session.tax, 4),
        formatValue(session.totalAmount, 4),
      ])
      // Remove completely empty rows
      .filter((row) =>
        row.some(
          (value) =>
            value !== null &&
            value !== undefined &&
            String(value).trim() !== "" &&
            String(value).trim() !== "-",
        ),
      );

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...exportRows]);

    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 18 },
      { wch: 25 },
      { wch: 18 },
      { wch: 22 },
      { wch: 22 },
      { wch: 22 },
      { wch: 15 },
      { wch: 25 },
      { wch: 16 },
      { wch: 16 },
      { wch: 32 },
      { wch: 24 },
      { wch: 24 },
      { wch: 25 },
      { wch: 24 },
      { wch: 16 },
      { wch: 20 },
    ];

    const headerStyle = {
      font: {
        bold: true,
        color: "000000",
        sz: 12,
      },
      fill: {
        patternType: "solid",
        fgColor: { rgb: "FFFFFF" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border: {
        top: { style: "thin", color: { rgb: "A6A6A6" } },
        bottom: { style: "thin", color: { rgb: "A6A6A6" } },
        left: { style: "thin", color: { rgb: "A6A6A6" } },
        right: { style: "thin", color: { rgb: "A6A6A6" } },
      },
    };

    const bodyStyle = {
      font: {
        color: "000000",
        sz: 11,
      },
      alignment: {
        vertical: "center",
        horizontal: "left",
      },
      border: {
        top: { style: "thin", color: { rgb: "A6A6A6" } },
        bottom: { style: "thin", color: { rgb: "A6A6A6" } },
        left: { style: "thin", color: { rgb: "A6A6A6" } },
        right: { style: "thin", color: { rgb: "A6A6A6" } },
      },
    };

    // Header styling
    headers.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: 0,
        c: colIndex,
      });

      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = headerStyle;
      }
    });

    // Body styling
    exportRows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({
          r: rowIndex + 1,
          c: colIndex,
        });

        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = bodyStyle;
        }
      });
    });

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Charging Sessions");

    XLSX.writeFile(workbook, "EV_Charging_Session_Report.xlsx");
  };

  /* ------------------------------ COLUMNS ------------------------------ */

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Session ID",
        size: 160,
      },

      {
        accessorKey: "stationId",
        header: "Station ID",
        size: 130,
      },

      {
        accessorKey: "stationName",
        header: "Station Name",
        size: 200,
      },

      {
        accessorKey: "connector",
        header: "Connector ID",
        size: 120,
        enableSorting: false,
      },

      {
        accessorKey: "user",
        header: "Mobile Number",
        size: 170,
      },

      {
        accessorKey: "start",
        header: "Start Time",
        size: 130,
      },

      {
        accessorKey: "end",
        header: "End Time",
        size: 130,
      },

      {
        accessorKey: "durationMin",
        header: "Duration",
        size: 130,
        Cell: ({ cell }) => fmtDuration(cell.getValue()),
      },

      {
        accessorKey: "energy",
        header: "Energy Delivered (kWh)",
        size: 170,
        muiTableHeadCellProps: headStyle,
        muiTableBodyCellProps: {
          align: "right",
          ...bodyStyle,
        },
        Cell: ({ cell }) => formatValue(cell.getValue(), 4),
      },

      {
        accessorKey: "startSoc",
        header: "Start SOC (%)",
        size: 150,
        muiTableHeadCellProps: headStyle,
        muiTableBodyCellProps: {
          align: "right",
          ...bodyStyle,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null || value === undefined ? "-" : `${value}%`;
        },
      },

      {
        accessorKey: "endSoc",
        header: "End SOC (%)",
        size: 150,
        muiTableHeadCellProps: headStyle,
        muiTableBodyCellProps: {
          align: "right",
          ...bodyStyle,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null || value === undefined ? "-" : `${value}%`;
        },
      },

      {
        accessorKey: "maxKw",
        header: "Max/Avg Charging Power (kW)",
        size: 220,
        muiTableHeadCellProps: headStyle,
        muiTableBodyCellProps: {
          align: "right",
          ...bodyStyle,
        },
        Cell: ({ row }) =>
          `${formatValue(row.original.maxKw, 2)} / ${formatValue(
            row.original.avgKw,
            2,
          )}`,
      },

      // {
      //   accessorKey: "status",
      //   header: "Session Status",
      //   size: 160,
      // },

      {
        accessorKey: "stopReason",
        header: "Stop Reason",
        size: 150,
        Cell: ({ cell }) => cell.getValue() || "-",
      },

      {
        accessorKey: "tariff",
        header: "Tariff Plan Applied",
        size: 190,
      },

      {
        accessorKey: "amount",
        header: "Cost/Amount Charged",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null ||
            value === undefined ||
            value === "" ||
            !Number.isFinite(Number(value))
            ? "-"
            : formatCurrency(Number(value));
        },
      },

      {
        accessorKey: "tax",
        header: "Tax",
        size: 110,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null ||
            value === undefined ||
            value === "" ||
            !Number.isFinite(Number(value))
            ? "-"
            : formatCurrency(Number(value));
        },
      },

      {
        accessorKey: "total",
        header: "Total Amount",
        size: 160,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return value === null ||
            value === undefined ||
            value === "" ||
            !Number.isFinite(Number(value))
            ? "-"
            : formatCurrency(Number(value));
        },
      },
    ],
    [],
  );

  return (
    <div id="csms">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h5 className="mb-3 fw-medium" style={{ fontSize: "15px" }}>
              Monitor Charging Sessions, Energy Usage and Status.
            </h5>
          </Col>

          {/* Filters */}
          <Col xs={12}>
            <div
              className="d-flex flex-column flex-md-row align-items-md-end gap-3 border p-3 mb-3"
              style={{
                borderRadius: "10px",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {/* Date Filters */}
              <div
                className="d-flex gap-3"
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div style={{ minWidth: "130px", flex: 1 }}>
                  <label
                    className="fw-semibold mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    FROM DATE
                  </label>

                  <Input
                    type="date"
                    value={fromDate}
                    // min={getCurrentMonthStart()}
                    max={toDate || getToday()}
                    onChange={(e) => {
                      setFromDate(e.target.value);

                      setPagination((p) => ({
                        ...p,
                        pageIndex: 0,
                      }));
                    }}
                    style={{
                      height: "36px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      width: "100%",
                    }}
                  />
                </div>

                <div style={{ minWidth: "130px", flex: 1 }}>
                  <label
                    className="fw-semibold mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    TO DATE
                  </label>

                  <Input
                    type="date"
                    value={toDate}
                    min={fromDate}
                    max={getToday()}
                    onChange={(e) => {
                      setToDate(e.target.value);

                      setPagination((p) => ({
                        ...p,
                        pageIndex: 0,
                      }));
                    }}
                    style={{
                      height: "36px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>

              {/* Search */}
              <div
                style={{
                  minWidth: "260px",
                  flex: 2,
                }}
              >
                <label
                  className="fw-semibold mb-1"
                  style={{ fontSize: "11px" }}
                >
                  SEARCH
                </label>

                <div className="position-relative">
                  <i
                    className="ri-search-line position-absolute text-muted"
                    style={{
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />

                  <Input
                    type="text"
                    placeholder="Session ID, station, user, status..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);

                      setPagination((p) => ({
                        ...p,
                        pageIndex: 0,
                      }));
                    }}
                    style={{
                      height: "36px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      paddingLeft: "32px",
                    }}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex gap-2 flex-shrink-0">
                <Button
                  color="light"
                  className="border rounded-1 text-nowrap fw-semibold d-flex align-items-center justify-content-center"
                  style={{
                    height: "36px",
                    fontSize: "12px",
                  }}
                  onClick={handleReset}
                >
                  <i className="ri-refresh-line me-1" />
                  Reset
                </Button>

                <Button
                  className="border rounded-1 text-nowrap fw-semibold d-flex align-items-center justify-content-center"
                  style={{
                    height: "36px",
                    fontSize: "12px",
                  }}
                  onClick={handleDownload}
                >
                  <i className="ri-download-2-line me-1" />
                  Download Excel
                </Button>
              </div>
            </div>
          </Col>

          {/* Summary cards */}
          {/* {summary.map((c) => (
            <Col key={c.label} xs={6} lg={3} xl={2} className="mb-3">
              <div className="border p-3 h-100" style={{ borderRadius: "8px" }}>
                <div
                  className="fw-semibold mb-1"
                  style={{
                    fontSize: "10px",
                    letterSpacing: ".05em",
                  }}
                >
                  {c.label}
                </div>

                <div
                  className="fw-bold"
                  style={{
                    fontSize: "20px",
                    lineHeight: 1.2,
                  }}
                >
                  {c.value}
                </div>

                <div className="text-muted mt-1" style={{ fontSize: "10px" }}>
                  {c.note}
                </div>
              </div>
            </Col>
          ))} */}

          {/* Table */}
          <Col lg={12}>
            <div
              className="border table-responsive"
              style={{ borderRadius: "4px" }}
            >
              {/* <div className="d-flex align-items-center gap-2 px-3 py-3">
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  Business Performance Log
                </span>

                <span
                  style={{
                    background: "#EEF2FF",
                    color: "#4338CA",
                    fontSize: "10px",
                    padding: "2px 8px",
                    borderRadius: "10px",
                  }}
                >
                  {totalCount} records
                </span>
              </div> */}

              <div>
                <MaterialReactTable
                  columns={columns}
                  data={data}
                  state={{
                    pagination,
                  }}
                  onPaginationChange={setPagination}
                  manualPagination
                  rowCount={totalCount}
                  columnResizeMode="on"
                  defaultColumn={{
                    minSize: 40,
                    maxSize: 1000,
                    size: 150,
                  }}
                  muiTableProps={{
                    sx: {
                      tableLayout: "fixed",
                      width: "100%",
                      minWidth: "2400px",
                    },
                  }}
                  muiTableHeadCellProps={headStyle}
                  muiTableBodyCellProps={bodyStyle}
                  muiTableBodyRowProps={{
                    sx: {
                      "&:nth-of-type(even) > td": {
                        backgroundColor: "#F9FAFB",
                      },
                    },
                  }}
                  muiPaginationProps={{
                    rowsPerPageOptions: [10, 25, 50, 75, 100],
                    showRowsPerPage: true,
                  }}
                  enableTopToolbar={false}
                  enableDensityToggle={false}
                  enableColumnActions={false}
                  enableFullScreenToggle={false}
                  enableColumnFilters={false}
                  enableSorting
                  muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: "0px",
                      boxShadow: "none",
                    },
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      {loading && <Loader />}
    </div>
  );
}

export default EVChargingSessionReport;
